import type { EChartsOption } from "echarts";
import { IBarData } from "apps/ai-metrics/types/aiMetrics.types";
import { AdaptedQuality } from "apps/ai-metrics/utils/dashboardAdapter";
import {
  createStackedBarOption,
  formatMonthLabels,
  SEVERITY_COLORS,
  SEVERITY_MAP,
  SERIES_COLORS,
} from "apps/ai-metrics/utils/echartOptions";
import { SectionChart } from "apps/ai-metrics/components/shared/SectionChart/SectionChart";
import { SectionHeader } from "apps/ai-metrics/components/shared/SectionHeader/SectionHeader";
import { StatCard } from "apps/ai-metrics/components/shared/StatCard/StatCard";
import { useSectionStyles } from "apps/ai-metrics/components/shared/useSectionStyles";
import {
  SECTION_NOTES,
  getMetricHelp,
  getChartHelp,
} from "apps/ai-metrics/config/metricHelpText";
import { useStyles as useChartsRowStyles } from "./OrganizationQualitySection.styles";

const CARD_META = [
  {
    label: "BUG DENSITY INDEX",
    subtitle: "Bugs opened",
    key: "bugDensityIndex" as const,
    higherIsBetter: false,
  },
  {
    label: "DEFECT ESCAPE RATE",
    subtitle: "Bugs reaching production",
    key: "defectEscapeRate" as const,
    higherIsBetter: false,
  },
  {
    label: "CRITICAL / BLOCKER BUGS",
    subtitle: "P1 + P2 volume",
    key: "criticalBugs" as const,
    higherIsBetter: false,
  },
  {
    label: "NON-PRODUCTION DEFECTS",
    subtitle: "Defects caught before production",
    key: "nonProductionDefects" as const,
    higherIsBetter: false,
  },
];

interface IOrganizationQualitySectionProps {
  kpis?: AdaptedQuality;
  loading?: boolean;
  bugSeverity?: IBarData;
  bugSeverityLoading?: boolean;
  bugSeverityError?: boolean;
}

export const OrganizationQualitySection = ({
  kpis,
  loading,
  bugSeverity,
  bugSeverityLoading,
  bugSeverityError,
}: IOrganizationQualitySectionProps): JSX.Element => {
  const styles = useSectionStyles();
  const chartsRowStyles = useChartsRowStyles();

  const bugSeverityChart: EChartsOption | undefined = bugSeverity
    ? createStackedBarOption({
        xLabels: formatMonthLabels(bugSeverity.labels),
        series: bugSeverity.series.map((s) => ({
          name: SEVERITY_MAP[s.name]?.label ?? s.name,
          data: s.values,
          color:
            s.color ?? SEVERITY_MAP[s.name]?.color ?? SEVERITY_COLORS.medium,
        })),
        yAxisLabel: "Bugs",
      })
    : undefined;

  return (
    <div className={styles.section}>
      <SectionHeader
        title="Quality - Organization View"
        note={SECTION_NOTES.organizationQuality}
      >
        <div className={styles.cardsRow}>
          {CARD_META.map(({ label, subtitle, key, higherIsBetter }) => (
            <StatCard
              key={label}
              label={label}
              subtitle={subtitle}
              higherIsBetter={higherIsBetter}
              loading={loading}
              {...(!loading ? (kpis?.[key] ?? { pending: true }) : {})}
              info={getMetricHelp(label)}
            />
          ))}
        </div>
        <div className={chartsRowStyles.chartsRow}>
          <SectionChart
            title="Bug & Defect Severity Split"
            option={
              bugSeverityChart
                ? { ...bugSeverityChart, legend: { show: false } }
                : undefined
            }
            loading={bugSeverityLoading}
            emptyMessage={
              !bugSeverityLoading && !bugSeverity
                ? bugSeverityError
                  ? "Failed to load data"
                  : "No data for selected period"
                : undefined
            }
            legend={bugSeverity?.series.map((s, i) => ({
              name: SEVERITY_MAP[s.name]?.label ?? s.name,
              color: s.color ?? SEVERITY_MAP[s.name]?.color ?? SERIES_COLORS[i],
            }))}
            info={getChartHelp("Bug & Defect Severity Split")}
          />
        </div>
      </SectionHeader>
    </div>
  );
};
